package com.schoolconnect.app.feature.reports

import com.schoolconnect.app.datasource.network.request.AcademicPerformanceReportRequest
import com.schoolconnect.app.datasource.network.request.AttendanceReportRequest
import com.schoolconnect.app.datasource.network.request.CombinedPerformanceReportRequest
import com.schoolconnect.app.datasource.network.response.AcademicPerformanceReportResponse
import com.schoolconnect.app.datasource.network.response.AttendanceReportResponse
import com.schoolconnect.app.datasource.network.response.CombinedPerformanceReportResponse
import com.schoolconnect.app.datasource.network.response.PerformanceCategoryResponse
import com.schoolconnect.app.repository.reports.ReportsRepository
import com.schoolconnect.app.repository.reports.StaffReportAttendanceStatusOption
import com.schoolconnect.app.repository.reports.StaffReportClassOption
import com.schoolconnect.app.repository.reports.StaffReportExamOption
import com.schoolconnect.app.repository.reports.StaffReportStudentOption
import com.schoolconnect.app.repository.reports.StaffReportStudentsPage
import com.schoolconnect.app.repository.reports.StaffReportsRepository
import com.schoolconnect.app.util.scInfo
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class StaffReportsUiState(
    val isLoadingClasses: Boolean = false,
    val classes: List<StaffReportClassOption> = emptyList(),
    val classError: String = "",
    val categories: List<PerformanceCategoryResponse> = emptyList(),
    val categoryError: String = "",
    val attendanceStatuses: List<StaffReportAttendanceStatusOption> = emptyList(),
    val attendanceStatusError: String = "",
    val isLoadingExams: Boolean = false,
    val exams: List<StaffReportExamOption> = emptyList(),
    val examError: String = "",
    val isLoadingStudents: Boolean = false,
    val students: List<StaffReportStudentOption> = emptyList(),
    val studentPageIndex: Int = 0,
    val studentTotalCount: Int = 0,
    val studentError: String = "",
    val report: StaffReportResultUi? = null,
    val isLoadingReport: Boolean = false,
    val reportError: String = "",
    val isExporting: Boolean = false,
    val feedback: String = ""
)

class StaffReportsViewModel(
    private val isTeacher: Boolean,
    private val schoolId: String,
    private val activeAcademicYearId: String
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main.immediate)
    private var lookupJob: Job? = null
    private var examJob: Job? = null
    private var studentJob: Job? = null
    private var reportJob: Job? = null
    private var exportJob: Job? = null

    private val _uiState = MutableStateFlow(StaffReportsUiState())
    val uiState: StateFlow<StaffReportsUiState> = _uiState.asStateFlow()

    fun loadLookups() {
        lookupJob?.cancel()
        lookupJob = scope.launch {
            scInfo("[REPORTS] lookup start")
            _uiState.update { it.copy(isLoadingClasses = true, classError = "") }
            val classResult = StaffReportsRepository.loadClassOptions(
                isTeacher = isTeacher,
                schoolId = schoolId,
                activeAcademicYearId = activeAcademicYearId
            )
            classResult.onSuccess { classes ->
                _uiState.update {
                    it.copy(
                        classes = classes,
                        classError = if (classes.isEmpty()) "No active reportable classes were found." else "",
                        isLoadingClasses = false
                    )
                }
            }.onFailure { throwable ->
                _uiState.update {
                    it.copy(
                        classes = emptyList(),
                        classError = throwable.message ?: "Unable to load reportable classes.",
                        isLoadingClasses = false
                    )
                }
            }

            ReportsRepository.getPerformanceCategories().onSuccess { categories ->
                val validCategories = categories.filter { !it.categoryName.isNullOrBlank() }
                _uiState.update { it.copy(categories = validCategories, categoryError = "") }
                scInfo("[REPORTS] category lookup success count=${validCategories.size}")
            }.onFailure { throwable ->
                _uiState.update {
                    it.copy(categories = emptyList(), categoryError = throwable.message ?: "Unable to load performance categories.")
                }
                scInfo("[REPORTS] category lookup failure errorType=${throwable::class.simpleName}")
            }

            StaffReportsRepository.loadAttendanceStatusOptions().onSuccess { statuses ->
                _uiState.update { it.copy(attendanceStatuses = statuses, attendanceStatusError = "") }
                scInfo("[REPORTS] attendance status lookup success count=${statuses.size}")
            }.onFailure { throwable ->
                _uiState.update {
                    it.copy(
                        attendanceStatuses = emptyList(),
                        attendanceStatusError = throwable.message ?: "Unable to load attendance-status options."
                    )
                }
                scInfo("[REPORTS] attendance status lookup failure errorType=${throwable::class.simpleName}")
            }
            scInfo("[REPORTS] lookup complete")
        }
    }

    fun loadExams(classIds: List<String>) {
        examJob?.cancel()
        examJob = scope.launch {
            _uiState.update { it.copy(isLoadingExams = true, examError = "", exams = emptyList()) }
            StaffReportsRepository.loadExamOptions(classIds).onSuccess { exams ->
                _uiState.update { it.copy(exams = exams, isLoadingExams = false) }
            }.onFailure { throwable ->
                _uiState.update {
                    it.copy(
                        exams = emptyList(),
                        examError = throwable.message ?: "Unable to load published exams.",
                        isLoadingExams = false
                    )
                }
            }
        }
    }

    fun loadStudents(classSectionId: String, query: String, pageIndex: Int) {
        studentJob?.cancel()
        studentJob = scope.launch {
            _uiState.update { it.copy(isLoadingStudents = true, studentError = "") }
            StaffReportsRepository.loadStudentsPage(
                isTeacher = isTeacher,
                schoolId = schoolId,
                classSectionId = classSectionId,
                query = query,
                pageIndex = pageIndex
            ).onSuccess { page -> setStudentPage(page, pageIndex) }.onFailure { throwable ->
                _uiState.update {
                    it.copy(
                        students = emptyList(),
                        studentPageIndex = pageIndex.coerceAtLeast(0),
                        studentTotalCount = 0,
                        studentError = throwable.message ?: "Unable to load students.",
                        isLoadingStudents = false
                    )
                }
            }
        }
    }

    fun clearReport() {
        reportJob?.cancel()
        _uiState.update { it.copy(report = null, isLoadingReport = false, reportError = "", feedback = "") }
    }

    fun runReport(query: StaffReportsQuery) {
        reportJob?.cancel()
        reportJob = scope.launch {
            _uiState.update { it.copy(report = null, isLoadingReport = true, reportError = "", feedback = "") }
            scInfo("[REPORTS] request start type=${query.type} view=${query.viewBy} classes=${query.classIds.size}")
            val result = when (query.type) {
                StaffReportType.ATTENDANCE -> {
                    val request = AttendanceReportRequest(
                        viewBy = query.viewBy,
                        classIds = query.classIds,
                        fromDate = query.fromDate,
                        toDate = query.toDate,
                        criteriaType = query.attendanceCriteriaType,
                        percentageOperator = query.attendanceOperator,
                        percentageValues = query.attendanceValues,
                        statusValue = query.attendanceStatus
                    )
                    ReportsRepository.generateAttendanceReport(request).map { mapAttendanceReport(it, query) }
                }
                StaffReportType.ACADEMIC -> {
                    val request = AcademicPerformanceReportRequest(
                        viewBy = query.viewBy,
                        classIds = query.classIds,
                        examIds = query.examIds.takeIf { it.isNotEmpty() },
                        fromDate = query.fromDate,
                        toDate = query.toDate,
                        criteriaType = query.academicCriteriaType,
                        percentageOperator = query.academicOperator,
                        percentageValues = query.academicValues,
                        categoryFilter = query.categoryFilter
                    )
                    ReportsRepository.generateAcademicPerformanceReport(request).map { mapAcademicReport(it, query) }
                }
                StaffReportType.COMBINED -> {
                    val request = CombinedPerformanceReportRequest(
                        viewBy = query.viewBy,
                        classIds = query.classIds,
                        studentId = query.studentId?.takeIf(String::isNotBlank),
                        fromDate = query.fromDate,
                        toDate = query.toDate,
                        criteria = query.combinedCriteria.takeIf { it.isNotEmpty() },
                        matchType = query.matchType
                    )
                    ReportsRepository.generateCombinedPerformanceReport(request).map { mapCombinedReport(it, query) }
                }
            }
            result.onSuccess { report ->
                scInfo("[REPORTS] request success type=${query.type} previewRows=${report.rows.size} total=${report.totalCount}")
                _uiState.update { it.copy(report = report, isLoadingReport = false) }
            }.onFailure { throwable ->
                scInfo("[REPORTS] request failure type=${query.type} errorType=${throwable::class.simpleName}")
                _uiState.update {
                    it.copy(report = null, reportError = throwable.message ?: "Unable to generate this report.", isLoadingReport = false)
                }
            }
        }
    }

    fun exportReport(query: StaffReportsQuery, format: String) {
        exportJob?.cancel()
        exportJob = scope.launch {
            _uiState.update { it.copy(isExporting = true, feedback = "Preparing $format export…") }
            val result = when (query.type) {
                StaffReportType.ATTENDANCE -> ReportsRepository.exportAttendanceReport(
                    AttendanceReportRequest(
                        viewBy = query.viewBy,
                        classIds = query.classIds,
                        fromDate = query.fromDate,
                        toDate = query.toDate,
                        criteriaType = query.attendanceCriteriaType,
                        percentageOperator = query.attendanceOperator,
                        percentageValues = query.attendanceValues,
                        statusValue = query.attendanceStatus
                    ),
                    format
                )
                StaffReportType.ACADEMIC -> ReportsRepository.exportAcademicPerformanceReport(
                    AcademicPerformanceReportRequest(
                        viewBy = query.viewBy,
                        classIds = query.classIds,
                        examIds = query.examIds.takeIf { it.isNotEmpty() },
                        fromDate = query.fromDate,
                        toDate = query.toDate,
                        criteriaType = query.academicCriteriaType,
                        percentageOperator = query.academicOperator,
                        percentageValues = query.academicValues,
                        categoryFilter = query.categoryFilter
                    ),
                    format
                )
                StaffReportType.COMBINED -> ReportsRepository.exportCombinedPerformanceReport(
                    CombinedPerformanceReportRequest(
                        viewBy = query.viewBy,
                        classIds = query.classIds,
                        studentId = query.studentId?.takeIf(String::isNotBlank),
                        fromDate = query.fromDate,
                        toDate = query.toDate,
                        criteria = query.combinedCriteria.takeIf { it.isNotEmpty() },
                        matchType = query.matchType
                    ),
                    format
                )
            }
            result.onSuccess {
                scInfo("[REPORTS] export success type=${query.type} format=$format bytes=${it.size}")
                _uiState.update { it.copy(isExporting = false, feedback = "$format export downloaded.") }
            }.onFailure { throwable ->
                scInfo("[REPORTS] export failure type=${query.type} format=$format errorType=${throwable::class.simpleName}")
                _uiState.update {
                    it.copy(isExporting = false, feedback = throwable.message ?: "Unable to download the $format report.")
                }
            }
        }
    }

    fun clear() {
        lookupJob?.cancel()
        examJob?.cancel()
        studentJob?.cancel()
        reportJob?.cancel()
        exportJob?.cancel()
        scope.cancel()
    }

    private fun setStudentPage(page: StaffReportStudentsPage, pageIndex: Int) {
        _uiState.update {
            it.copy(
                students = page.items,
                studentPageIndex = pageIndex.coerceAtLeast(0),
                studentTotalCount = page.totalCount,
                studentError = "",
                isLoadingStudents = false
            )
        }
    }

    private fun mapAttendanceReport(
        response: AttendanceReportResponse,
        query: StaffReportsQuery
    ): StaffReportResultUi {
        val summary = requireNotNull(response.summary) { "Attendance report response is missing its summary." }
        val total = requireNotNull(response.pagination?.count) { "Attendance report response is missing its total count." }
        val preview = response.reportPreview.orEmpty()
        if (total > 0 && preview.isEmpty()) error("Attendance report returned a total count but no preview rows.")
        val schoolView = query.viewBy.name == "SCHOOL"
        val headers = if (schoolView) {
            listOf("Class", "Students", "Working Days", "Present Days", "Absent Days", "Attendance %", "Status")
        } else {
            listOf("Class", "Roll No.", "Student", "Present Days", "Absent Days", "A-M", "A-A", "Attendance %", "Status")
        }
        val rows = preview.map { row ->
            val className = required(row.className, "Attendance report row is missing class_name.")
            val sectionName = required(row.sectionName, "Attendance report row is missing section_name.")
            if (schoolView) {
                listOf(
                    "$className-$sectionName",
                    required(row.students, "Attendance report row is missing students.").toString(),
                    required(row.workingDays, "Attendance report row is missing working_days.").toString(),
                    decimal(row.presentDays),
                    decimal(row.absentDays),
                    percent(row.attendancePercentage),
                    required(row.status, "Attendance report row is missing status.")
                )
            } else {
                listOf(
                    "$className-$sectionName",
                    row.rollNumber.orEmpty(),
                    required(row.studentName, "Attendance report row is missing student_name."),
                    decimal(row.presentDays),
                    decimal(row.absentDays),
                    decimal(row.absentMorning),
                    decimal(row.absentAfternoon),
                    percent(row.attendancePercentage),
                    required(row.status, "Attendance report row is missing status.")
                )
            }
        }
        return StaffReportResultUi(
            title = "Attendance Report",
            headers = headers,
            rows = rows,
            totalCount = total,
            summary = listOf(
                StaffReportSummaryItem("Total Students", required(summary.totalStudents, "Attendance report summary is missing total_students.").toString(), "In selected scope"),
                StaffReportSummaryItem("Working Days", required(summary.workingDays, "Attendance report summary is missing working_days.").toString(), "Selected reporting period"),
                StaffReportSummaryItem("Overall Attendance", percent(summary.overallAttendancePercentage), "Across selected scope"),
                StaffReportSummaryItem(
                    required(summary.matchingRecordsLabel, "Attendance report summary is missing matching_records_label."),
                    required(summary.matchingRecordsCount, "Attendance report summary is missing matching_records_count.").toString(),
                    "Matching records"
                )
            )
        )
    }

    private fun mapAcademicReport(
        response: AcademicPerformanceReportResponse,
        query: StaffReportsQuery
    ): StaffReportResultUi {
        val summary = requireNotNull(response.summary) { "Academic report response is missing its summary." }
        val total = requireNotNull(response.pagination?.count) { "Academic report response is missing its total count." }
        val preview = response.reportPreview.orEmpty()
        if (total > 0 && preview.isEmpty()) error("Academic report returned a total count but no preview rows.")
        val schoolView = query.viewBy.name == "SCHOOL"
        val categoryNames = summary.categorySummary.orEmpty().map {
            required(it.categoryName, "Academic report category summary is missing category_name.")
        }
        val headers = if (schoolView) {
            listOf("Class", "Students", "Exams", "Exam Average %") + categoryNames
        } else {
            listOf("Class", "Roll No.", "Student", "Marks", "Max Marks", "Exam %", "Category", "Rank")
        }
        val rows = preview.map { row ->
            val className = required(row.className, "Academic report row is missing class_name.")
            val sectionName = required(row.sectionName, "Academic report row is missing section_name.")
            if (schoolView) {
                val counts = row.categoryCounts.orEmpty()
                listOf(
                    "$className-$sectionName",
                    required(row.students, "Academic report row is missing students.").toString(),
                    required(row.examsCount, "Academic report row is missing exams_count.").toString(),
                    percent(row.examAveragePercentage)
                ) + categoryNames.map { counts[it]?.toString() ?: "0" }
            } else {
                listOf(
                    "$className-$sectionName",
                    row.rollNumber.orEmpty(),
                    required(row.studentName, "Academic report row is missing student_name."),
                    decimal(row.marksObtained),
                    decimal(row.maxMarks),
                    percent(row.examPercentage),
                    row.performanceCategory.orEmpty(),
                    row.rank?.toString().orEmpty()
                )
            }
        }
        return StaffReportResultUi(
            title = "Academic Performance Report",
            headers = headers,
            rows = rows,
            totalCount = total,
            summary = listOf(
                StaffReportSummaryItem("Total Students", required(summary.totalStudents, "Academic report summary is missing total_students.").toString(), "In selected scope"),
                StaffReportSummaryItem("Published Exams", required(summary.totalExams, "Academic report summary is missing total_exams.").toString(), "Selected reporting period"),
                StaffReportSummaryItem("Exam Average", percent(summary.overallExamAverage), "Across selected scope"),
                StaffReportSummaryItem(
                    required(summary.matchingRecordsLabel, "Academic report summary is missing matching_records_label."),
                    required(summary.matchingRecordsCount, "Academic report summary is missing matching_records_count.").toString(),
                    "Matching records"
                )
            ) + summary.categorySummary.orEmpty().map {
                StaffReportSummaryItem(
                    required(it.categoryName, "Academic report category summary is missing category_name."),
                    required(it.studentCount, "Academic report category summary is missing student_count.").toString(),
                    it.rangeLabel.orEmpty()
                )
            }
        )
    }

    private fun mapCombinedReport(
        response: CombinedPerformanceReportResponse,
        query: StaffReportsQuery
    ): StaffReportResultUi {
        val summary = requireNotNull(response.summary) { "Combined report response is missing its summary." }
        val total = requireNotNull(response.pagination?.count) { "Combined report response is missing its total count." }
        val preview = response.reportPreview.orEmpty()
        if (total > 0 && preview.isEmpty()) error("Combined report returned a total count but no preview rows.")
        val headers = when (query.viewBy.name) {
            "SCHOOL" -> listOf("Class", "Students", "Attendance %", "Attendance Category", "Exam Average %", "Exam Category")
            "STUDENT" -> listOf("Student", "Roll No.", "Class", "Attendance %", "Attendance Category", "Present Days", "Absent Days", "Exam Average %", "Exam Category", "Exams", "Marks")
            else -> listOf("Class", "Roll No.", "Student", "Attendance %", "Attendance Category", "Exam Average %", "Exam Category")
        }
        val rows = preview.map { row ->
            val className = required(row.className, "Combined report row is missing class_name.")
            val sectionName = required(row.sectionName, "Combined report row is missing section_name.")
            when (query.viewBy.name) {
                "SCHOOL" -> listOf(
                    "$className-$sectionName",
                    required(row.students, "Combined report row is missing students.").toString(),
                    percent(row.attendancePercentage),
                    required(row.attendanceCategory, "Combined report row is missing attendance_category."),
                    percent(row.examAveragePercentage),
                    required(row.examCategory, "Combined report row is missing exam_category.")
                )
                "STUDENT" -> listOf(
                    required(row.studentName, "Combined report row is missing student_name."),
                    row.rollNumber.orEmpty(),
                    "$className-$sectionName",
                    percent(row.attendancePercentage),
                    required(row.attendanceCategory, "Combined report row is missing attendance_category."),
                    decimal(row.presentDays),
                    decimal(row.absentDays),
                    percent(row.examAveragePercentage),
                    row.examCategory.orEmpty(),
                    row.examsCount?.toString().orEmpty(),
                    if (row.totalMarksObtained == null || row.totalMaxMarks == null) "—" else "${decimal(row.totalMarksObtained)} / ${decimal(row.totalMaxMarks)}"
                )
                else -> listOf(
                    "$className-$sectionName",
                    row.rollNumber.orEmpty(),
                    required(row.studentName, "Combined report row is missing student_name."),
                    percent(row.attendancePercentage),
                    required(row.attendanceCategory, "Combined report row is missing attendance_category."),
                    percent(row.examAveragePercentage),
                    row.examCategory.orEmpty()
                )
            }
        }
        return StaffReportResultUi(
            title = "Combined Performance Report",
            headers = headers,
            rows = rows,
            totalCount = total,
            summary = listOf(
                StaffReportSummaryItem("Total Students", required(summary.totalStudents, "Combined report summary is missing total_students.").toString(), "In selected scope"),
                StaffReportSummaryItem("Overall Attendance", percent(summary.overallAttendancePercentage), "Across selected scope"),
                StaffReportSummaryItem("Exam Average", percent(summary.overallExamAverage), "Published marks"),
                StaffReportSummaryItem(
                    required(summary.matchingRecordsLabel, "Combined report summary is missing matching_records_label."),
                    required(summary.matchingRecordsCount, "Combined report summary is missing matching_records_count.").toString(),
                    "Matching records"
                )
            )
        )
    }

    private fun decimal(value: Double?): String = value?.let { formatDecimal(it) } ?: "—"
    private fun percent(value: Double?): String = value?.let { "${formatDecimal(it)}%" } ?: "—"
    private fun formatDecimal(value: Double): String {
        val rounded = kotlin.math.round(value * 10.0) / 10.0
        return if (rounded % 1.0 == 0.0) rounded.toInt().toString() else rounded.toString()
    }

    private fun <T : Any> required(value: T?, message: String): T = requireNotNull(value) { message }
}

internal fun toReportApiDate(value: String): String? {
    val parts = value.trim().split("\\s+".toRegex())
    if (parts.size != 3) return null
    val day = parts[0].toIntOrNull()?.takeIf { it in 1..31 } ?: return null
    val month = when (parts[1].lowercase()) {
        "jan" -> 1
        "feb" -> 2
        "mar" -> 3
        "apr" -> 4
        "may" -> 5
        "jun" -> 6
        "jul" -> 7
        "aug" -> 8
        "sep" -> 9
        "oct" -> 10
        "nov" -> 11
        "dec" -> 12
        else -> return null
    }
    val year = parts[2].toIntOrNull()?.takeIf { it in 1..9999 } ?: return null
    val maxDay = when (month) {
        2 -> if (year % 400 == 0 || (year % 4 == 0 && year % 100 != 0)) 29 else 28
        4, 6, 9, 11 -> 30
        else -> 31
    }
    if (day > maxDay) return null
    return "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
}
